/**
 * bakeGraphicsToSprite — the texture-baking helper (BUILD_STANDARDS gotcha:
 * "draw once, animate by transform"). Heavy vector geometry (silhouettes,
 * complex shapes) is expensive to keep alive as a Graphics — the GPU
 * re-tessellates every frame. Baking draws the geometry ONCE into a
 * RenderTexture, then hands back a lightweight ISprite that samples that
 * texture. The sprite transforms cheaply (x/y/scale/alpha = 4 uniforms);
 * the geometry is never re-evaluated.
 *
 * When to use:
 *  - Leviathans / large silhouettes (cathedral-scale bone shapes)
 *  - Any effect that draws complex static geometry once then animates
 *    by transform only
 *  - NOT for effects that redraw per-frame (particles, weather, etc.)
 *
 * Degradation (BUILD_STANDARDS §3): if the bake factory isn't registered
 * (headless / cold-load / test), returns null → caller skips the layer
 * (quieter scene, never a crash). The caller MUST null-check.
 *
 * @see docs/design/systems/texture-baking-helper.md — blueprint.
 */
import type { IGraphics, ISprite, GraphicsStyle, SpriteOptions } from '../../../rendering/IRenderer';

/** Options for bakeGraphicsToSprite. */
export interface BakeOptions {
  /** The draw callback: receives a fresh IGraphics, draws geometry into it.
   *  The graphics is destroyed after baking — don't hold a reference. */
  draw: (gfx: IGraphics) => void;
  /** Graphics style (depth, parent, blend mode). The graphics is created
   *  with this style, drawn into, then baked. The resulting sprite inherits
   *  the depth + parent from spriteOpts. */
  graphicsStyle?: GraphicsStyle;
  /** Sprite options for the baked result. x/y/width/height/depth/parent
   *  from here; initialTextureKey is ignored (the bake generates its own). */
  spriteOpts: Omit<SpriteOptions, 'initialTextureKey'>;
  /** Optional texture key for caching (if the same geometry is baked
   *  multiple times — e.g. same seed silhouette). If omitted, a unique
   *  key is generated. */
  textureKey?: string;
}

type BakeFactory = (opts: BakeOptions) => ISprite | null;

let bakeFactory: BakeFactory | null = null;

/** Register the Pixi bake implementation (called at boot by
 *  PixiFactoriesRegistry). The impl uses app.renderer.generateTexture(). */
export function registerBakeGraphicsFactory(fn: BakeFactory): void {
  bakeFactory = fn;
}

/** Test-only: reset the factory slot. */
export function __resetBakeGraphicsFactoryForTesting(): void {
  bakeFactory = null;
}

/**
 * Bake vector geometry (drawn via the callback) into a RenderTexture, then
 * return a lightweight ISprite sampling that texture. The intermediate
 * IGraphics is destroyed.
 *
 * Returns null if the bake factory isn't registered (headless / test) →
 * caller MUST null-check and skip gracefully.
 */
export function bakeGraphicsToSprite(opts: BakeOptions): ISprite | null {
  if (!bakeFactory) return null; // headless / cold-load → quieter scene
  return bakeFactory(opts);
}
